import { useState } from "react";
import { ChevronLeft, ChevronRight, Image as ImageIcon, Play } from "lucide-react";
import { Image } from "@/components/Image";
import { cn } from "@/utils/helpers";

/** One file of a multi-file post. Structurally the same as `ReferenceMediaItem`. */
export interface MediaCarouselItem {
  mediaType: "video" | "image";
  url: string | null;
  /** A video slide's cover frame. */
  posterUrl: string | null;
}

const ARROW =
  "absolute top-1/2 z-10 grid size-8 -translate-y-1/2 place-items-center border border-white/40 bg-black/60 text-white transition-colors hover:bg-black/85";

/**
 * Pages through a post with more than one file — a pasted Instagram carousel —
 * one slide at a time, filling whatever frame its host gives it.
 *
 * Plain black-and-white chrome rather than `--co-*` tokens: it renders on the
 * cockpit, inside the client portal and on the dark media stages alike, and only
 * the first has the tokens in scope.
 *
 * A video slide shows its cover until played, then plays in place with native
 * controls. Changing slide unmounts the player, which is what stops it.
 */
export function MediaCarousel({
  items,
  label,
  fit = "contain",
  className,
}: {
  items: MediaCarouselItem[];
  /** Names the post for assistive tech and each slide's alt text. */
  label: string;
  fit?: "contain" | "cover";
  className?: string;
}) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);

  const count = items.length;
  if (count === 0) return null;

  const position = Math.min(index, count - 1);
  const slide = items[position];
  const slideLabel = count > 1 ? `${label}, slide ${position + 1} of ${count}` : label;
  const mediaClass = cn(
    "absolute inset-0 size-full",
    fit === "cover" ? "object-cover" : "object-contain",
  );

  const go = (step: number) => {
    setPlaying(false);
    setIndex((position + step + count) % count);
  };

  return (
    <div
      role="group"
      aria-roledescription="carousel"
      aria-label={label}
      tabIndex={count > 1 ? 0 : undefined}
      onKeyDown={
        count > 1
          ? (event) => {
              if (event.key === "ArrowLeft") {
                event.preventDefault();
                go(-1);
              } else if (event.key === "ArrowRight") {
                event.preventDefault();
                go(1);
              }
            }
          : undefined
      }
      className={cn(
        "relative size-full overflow-hidden bg-black outline-none focus-visible:ring-2 focus-visible:ring-white/60",
        className,
      )}
    >
      {slide.mediaType === "video" && slide.url ? (
        playing ? (
          <video
            key={slide.url}
            src={slide.url}
            poster={slide.posterUrl ?? undefined}
            controls
            autoPlay
            playsInline
            className="absolute inset-0 size-full object-contain"
          />
        ) : (
          <>
            {slide.posterUrl ? (
              <Image key={slide.posterUrl} src={slide.posterUrl} alt={slideLabel} className={mediaClass} />
            ) : (
              // `#t=0.1` seeks to and paints a frame; preload="metadata" alone
              // often shows nothing until play.
              <video
                key={slide.url}
                src={`${slide.url}#t=0.1`}
                muted
                playsInline
                preload="metadata"
                className={mediaClass}
              />
            )}
            <button
              type="button"
              onClick={() => setPlaying(true)}
              aria-label={`Play ${slideLabel}`}
              className="absolute inset-0 grid place-items-center bg-black/10 transition-colors hover:bg-black/30"
            >
              <span className="co-glass-clear grid size-11 place-items-center rounded-pill text-white">
                <Play className="size-4 translate-x-px" fill="currentColor" strokeWidth={0} />
              </span>
            </button>
          </>
        )
      ) : slide.url ? (
        <Image key={slide.url} src={slide.url} alt={slideLabel} className={mediaClass} />
      ) : (
        <span className="absolute inset-0 grid place-items-center text-white/45">
          <ImageIcon className="size-6" strokeWidth={1.4} />
        </span>
      )}

      {count > 1 ? (
        <>
          <button type="button" onClick={() => go(-1)} aria-label="Previous slide" className={cn(ARROW, "left-2")}>
            <ChevronLeft className="size-4" />
          </button>
          <button type="button" onClick={() => go(1)} aria-label="Next slide" className={cn(ARROW, "right-2")}>
            <ChevronRight className="size-4" />
          </button>
          <span
            aria-live="polite"
            className="pointer-events-none absolute right-2 top-2 z-10 rounded-tag bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums tracking-[0.08em] text-white/90"
          >
            {position + 1} / {count}
          </span>
        </>
      ) : null}
    </div>
  );
}

export default MediaCarousel;
